/**
 * VH-207: the module page's per-location numbers.
 *
 * A READ-ONLY VIEWER (decided 2026-09-09). Picking a location here changes only
 * what is displayed — which variant a session loads is decided by the
 * session's location, which VH-212 puts in the athlete's hands. A second control
 * here would be a way to train the wrong gym's numbers on purpose, so this
 * component deliberately owns no state that reaches the server except the two
 * explicit variant actions: reset (re-seed) and delete.
 *
 * Hidden entirely when the module has no variants — a user with one gym, or a
 * module never trained anywhere, sees nothing new.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { MapPinIcon, ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline'
import { weeklyProgramsAPI } from '../../services/api'

const moduleVariantsKey = (templateId) => ['moduleVariants', templateId]

// A number worth aiming for: present and above 0. Bodyweight rows carry 0 kg,
// which prescribes nothing — same as an empty field (UAT 2026-09-15).
const isAimable = (weight) => weight != null && weight !== '' && Number(weight) > 0

function WeightCell({ weight, isTarget, testId }) {
  const { t } = useTranslation()
  if (weight == null || weight === '') {
    return <span className="text-gray-400" data-testid={testId}>-</span>
  }
  return isTarget && isAimable(weight) ? (
    <span
      data-testid={`${testId}-target`}
      title={t('variants.targetExplain')}
      className="inline-block rounded border border-dashed border-primary-400 px-1.5 text-primary-700 italic"
    >
      {weight}
      <span className="sr-only"> ({t('variants.target')})</span>
    </span>
  ) : (
    <span data-testid={testId} className="inline-block px-1.5 font-medium text-gray-900">
      {weight}
    </span>
  )
}

function ExerciseNumbers({ exercise, targetFlags = [], showMachine = false }) {
  const { t } = useTranslation()
  const weights = exercise.weight_per_set || []
  const name = exercise.substitute_exercise_name || exercise.exercise_name
  return (
    <li
      className="flex flex-wrap items-baseline justify-between gap-2 py-2"
      data-testid={`variant-exercise-${exercise.template_exercise_id}`}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 break-words">{name}</p>
        {exercise.substitute_exercise_name && (
          <p className="text-xs text-gray-500">
            {t('variants.standsInFor', { name: exercise.exercise_name })}
          </p>
        )}
        {showMachine && exercise.performed_equipment_name && (
          <p className="text-xs text-gray-500">{exercise.performed_equipment_name}</p>
        )}
      </div>
      <div className="flex flex-wrap gap-1 text-sm">
        {weights.length === 0 ? (
          <span className="text-gray-400">-</span>
        ) : (
          weights.map((w, i) => (
            <WeightCell
              key={i}
              weight={w}
              isTarget={Boolean(targetFlags[i])}
              testId={`variant-weight-${exercise.template_exercise_id}-${i}`}
            />
          ))
        )}
        <span className="text-xs text-gray-500 self-center">kg</span>
      </div>
    </li>
  )
}

export default function ModuleVariantViewer({ templateId }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  // '' = the module's own base numbers.
  const [viewing, setViewing] = useState('')
  const [actionError, setActionError] = useState(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: moduleVariantsKey(templateId),
    queryFn: () => weeklyProgramsAPI.getModuleVariants(templateId).then((r) => r.data),
    enabled: templateId != null,
  })

  const afterAction = (removed) => {
    setActionError(null)
    if (removed) setViewing('')
    queryClient.invalidateQueries({ queryKey: moduleVariantsKey(templateId) })
  }
  const onError = () => setActionError(t('variants.actionFailed'))

  const resetMutation = useMutation({
    mutationFn: (locationId) => weeklyProgramsAPI.resetModuleVariant(templateId, locationId),
    onSuccess: () => afterAction(false),
    onError,
  })
  const deleteMutation = useMutation({
    mutationFn: (locationId) => weeklyProgramsAPI.deleteModuleVariant(templateId, locationId),
    onSuccess: () => afterAction(true),
    onError,
  })

  // Loading/error degrade to showing nothing: the module's own table above is
  // the base numbers already, so a failed viewer loses no information.
  if (isLoading || isError || !data) return null
  const locations = data.locations || []
  if (locations.length === 0) return null

  const current = locations.find((l) => String(l.location_id) === String(viewing)) || null
  // The legend explains a marker, so it appears only when a marker is actually
  // SHOWN — a flagged entry with no weight renders "-", not Aim (UAT 2026-09-14).
  const hasTargets = current?.exercises?.some((e) =>
    (e.target_flags || []).some((flag, i) => flag && isAimable((e.weight_per_set || [])[i]))
  )
  const busy = resetMutation.isPending || deleteMutation.isPending

  return (
    <section className="mb-4 rounded-lg border border-gray-200 p-3" data-testid="module-variant-viewer">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="flex items-center gap-1 text-sm font-semibold text-gray-900">
          <MapPinIcon className="h-4 w-4" />
          {t('variants.title')}
        </h4>
        <div className="flex flex-wrap gap-1" role="tablist" aria-label={t('variants.title')}>
          <button
            type="button"
            role="tab"
            aria-selected={viewing === ''}
            onClick={() => setViewing('')}
            data-testid="variant-tab-base"
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              viewing === '' ? 'bg-gray-900 text-on-neutral' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('variants.base')}
          </button>
          {locations.map((loc) => {
            const selected = String(viewing) === String(loc.location_id)
            return (
              <button
                key={loc.location_id}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setViewing(String(loc.location_id))}
                data-testid={`variant-tab-${loc.location_id}`}
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  selected ? 'bg-primary-600 text-on-primary' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {loc.location_name}
              </button>
            )
          })}
        </div>
      </div>

      <p className="mt-2 text-xs text-gray-500">
        {current
          ? t('variants.viewingLocation', { location: current.location_name })
          : t('variants.viewingBase')}
      </p>

      <ul className="mt-1 divide-y divide-gray-100">
        {(current ? current.exercises : data.base || []).map((ex) => (
          <ExerciseNumbers
            key={ex.template_exercise_id}
            exercise={ex}
            targetFlags={current ? ex.target_flags : []}
            showMachine={Boolean(current)}
          />
        ))}
      </ul>

      {hasTargets && (
        <p className="mt-2 text-xs text-gray-600" data-testid="variant-target-legend">
          <span className="mr-1 inline-block rounded border border-dashed border-primary-400 px-1 italic text-primary-700">
            {t('variants.target')}
          </span>
          {t('variants.targetExplain')}
        </p>
      )}

      {current && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            data-testid="variant-reset"
            onClick={() => {
              if (window.confirm(t('variants.resetConfirm', { location: current.location_name }))) {
                resetMutation.mutate(current.location_id)
              }
            }}
            className="btn-outline btn-sm flex items-center disabled:opacity-50"
          >
            <ArrowPathIcon className="mr-1 h-4 w-4" />
            {t('variants.reset')}
          </button>
          <button
            type="button"
            disabled={busy}
            data-testid="variant-delete"
            onClick={() => {
              if (window.confirm(t('variants.deleteConfirm', { location: current.location_name }))) {
                deleteMutation.mutate(current.location_id)
              }
            }}
            className="btn-outline btn-sm flex items-center text-red-600 disabled:opacity-50"
          >
            <TrashIcon className="mr-1 h-4 w-4" />
            {t('variants.delete')}
          </button>
        </div>
      )}
      {actionError && (
        <p className="mt-2 text-sm text-red-600" data-testid="variant-action-error">{actionError}</p>
      )}
    </section>
  )
}
